import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Container,
  useScrollTrigger,
  alpha,
  useTheme,
  useMediaQuery,
  Divider,
  Menu,
  MenuItem,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Login as LoginIcon,
  ArrowForward,
  KeyboardArrowDown,
  Business,
  Analytics,
  ExpandLess,
  ExpandMore,
  Download,
} from '@mui/icons-material';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Features', path: '/features' },
  {
    label: 'Solutions',
    path: '/solutions',
    hasDropdown: true,
    dropdownId: 'solutions',
    dropdownItems: [
      { label: 'By Industry', path: '/solutions', icon: Business },
      { label: 'Analytics as a Service', path: '/aaas', icon: Analytics },
    ],
  },
  { label: 'Pricing', path: '/pricing' },
  { label: 'About', path: '/about' },
  {
    label: 'Support',
    path: '/support',
    hasDropdown: true,
    dropdownId: 'support',
    dropdownItems: [
      { label: 'Download App', path: '/download', icon: Download },
    ],
  },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState({ id: null, element: null });
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(null); // Track which submenu is open by dropdownId
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Detect scroll for background transition
  const scrollTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 50,
  });

  const isHomePage = location.pathname === '/';
  const showTransparent = isHomePage && !scrollTrigger;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
    setMobileSubmenuOpen(null);
  };

  const handleDropdownOpen = (event, dropdownId) => {
    setDropdownAnchor({ id: dropdownId, element: event.currentTarget });
  };

  const handleDropdownClose = () => {
    setDropdownAnchor({ id: null, element: null });
  };

  const handleMobileSubmenuToggle = (dropdownId) => {
    setMobileSubmenuOpen(mobileSubmenuOpen === dropdownId ? null : dropdownId);
  };

  const isActive = (path) => {
    if (path === '/solutions') {
      return location.pathname.startsWith('/solutions') || location.pathname === '/aaas';
    }
    if (path === '/support') {
      return location.pathname === '/download';
    }
    return location.pathname === path;
  };

  // Mobile drawer content
  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          component={Link}
          to="/"
          onClick={handleDrawerToggle}
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <Box
            component="img"
            src="/geoROAD_v2.png"
            alt="geoROADS.ai"
            sx={{ height: 36 }}
          />
        </Box>
        <IconButton onClick={handleDrawerToggle} edge="end">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Navigation Links */}
      <List sx={{ flex: 1, py: 2 }}>
        {navLinks.map((link) => (
          <React.Fragment key={link.path}>
            {link.hasDropdown ? (
              <>
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => handleMobileSubmenuToggle(link.dropdownId)}
                    selected={isActive(link.path)}
                    sx={{
                      py: 2,
                      px: 3,
                      '&.Mui-selected': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        borderRight: `3px solid ${theme.palette.primary.main}`,
                      },
                    }}
                  >
                    <ListItemText
                      primary={link.label}
                      primaryTypographyProps={{
                        fontWeight: isActive(link.path) ? 600 : 400,
                        fontSize: '1.1rem',
                      }}
                    />
                    {mobileSubmenuOpen === link.dropdownId ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={mobileSubmenuOpen === link.dropdownId} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {link.dropdownItems.map((item) => (
                      <ListItemButton
                        key={item.path}
                        component={Link}
                        to={item.path}
                        onClick={handleDrawerToggle}
                        selected={location.pathname === item.path}
                        sx={{
                          py: 1.5,
                          pl: 5,
                          pr: 3,
                          '&.Mui-selected': {
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <item.icon sx={{ fontSize: 20, color: 'primary.main' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontWeight: location.pathname === item.path ? 600 : 400,
                            fontSize: '1rem',
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              <ListItem disablePadding>
                <ListItemButton
                  component={Link}
                  to={link.path}
                  onClick={handleDrawerToggle}
                  selected={isActive(link.path)}
                  sx={{
                    py: 2,
                    px: 3,
                    '&.Mui-selected': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      borderRight: `3px solid ${theme.palette.primary.main}`,
                    },
                  }}
                >
                  <ListItemText
                    primary={link.label}
                    primaryTypographyProps={{
                      fontWeight: isActive(link.path) ? 600 : 400,
                      fontSize: '1.1rem',
                    }}
                  />
                </ListItemButton>
              </ListItem>
            )}
          </React.Fragment>
        ))}
      </List>

      <Divider />

      {/* CTAs */}
      <Box sx={{ p: 3 }}>
        <Button
          component={Link}
          to="/contact"
          variant="contained"
          fullWidth
          onClick={handleDrawerToggle}
          sx={{
            mb: 2,
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 2,
            textTransform: 'none',
            bgcolor: 'primary.main',
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          Contact Sales
        </Button>
        <Button
          href="https://app.georoad.ai/login"
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          fullWidth
          onClick={handleDrawerToggle}
          sx={{
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 2,
            textTransform: 'none',
            borderColor: 'primary.main',
            color: 'primary.main',
            '&:hover': {
              borderColor: 'primary.dark',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
            },
          }}
        >
          Log In
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={showTransparent ? 0 : 1}
        sx={{
          backgroundColor: showTransparent
            ? 'transparent'
            : alpha(theme.palette.background.paper, 0.95),
          backdropFilter: showTransparent ? 'none' : 'blur(10px)',
          transition: 'all 0.3s ease-in-out',
          borderBottom: showTransparent
            ? 'none'
            : `1px solid ${theme.palette.divider}`,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 64, md: 72 },
              justifyContent: 'space-between',
            }}
          >
            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
              }}
            >
              <Box
                component="img"
                src="/geoROAD_v2.png"
                alt="geoROADS.ai"
                sx={{
                  height: { xs: 32, md: 40 },
                  filter: showTransparent ? 'brightness(0) invert(1)' : 'none',
                  transition: 'filter 0.3s ease',
                }}
              />
            </Box>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {navLinks.map((link) =>
                  link.hasDropdown ? (
                    <React.Fragment key={link.path}>
                      <Button
                        onClick={(e) => handleDropdownOpen(e, link.dropdownId)}
                        endIcon={<KeyboardArrowDown sx={{ fontSize: 18 }} />}
                        sx={{
                          color: showTransparent
                            ? 'white'
                            : isActive(link.path)
                            ? 'primary.main'
                            : 'text.primary',
                          fontWeight: isActive(link.path) ? 600 : 500,
                          px: 2,
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 6,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: isActive(link.path) ? '60%' : 0,
                            height: 2,
                            bgcolor: showTransparent ? 'white' : 'primary.main',
                            borderRadius: 1,
                            transition: 'width 0.2s ease',
                          },
                          '&:hover::after': {
                            width: '60%',
                          },
                        }}
                      >
                        {link.label}
                      </Button>
                      <Menu
                        anchorEl={dropdownAnchor.id === link.dropdownId ? dropdownAnchor.element : null}
                        open={dropdownAnchor.id === link.dropdownId}
                        onClose={handleDropdownClose}
                        MenuListProps={{
                          sx: { py: 1 },
                        }}
                        PaperProps={{
                          elevation: 3,
                          sx: {
                            mt: 1,
                            minWidth: 220,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                          },
                        }}
                        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                      >
                        {link.dropdownItems.map((item) => (
                          <MenuItem
                            key={item.path}
                            component={Link}
                            to={item.path}
                            onClick={handleDropdownClose}
                            selected={location.pathname === item.path}
                            sx={{
                              py: 1.5,
                              px: 2,
                              gap: 1.5,
                              '&.Mui-selected': {
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                              },
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.04),
                              },
                            }}
                          >
                            <item.icon sx={{ fontSize: 20, color: 'primary.main' }} />
                            {item.label}
                          </MenuItem>
                        ))}
                      </Menu>
                    </React.Fragment>
                  ) : (
                    <Button
                      key={link.path}
                      component={Link}
                      to={link.path}
                      sx={{
                        color: showTransparent
                          ? 'white'
                          : isActive(link.path)
                          ? 'primary.main'
                          : 'text.primary',
                        fontWeight: isActive(link.path) ? 600 : 500,
                        px: 2,
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 6,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: isActive(link.path) ? '60%' : 0,
                          height: 2,
                          bgcolor: showTransparent ? 'white' : 'primary.main',
                          borderRadius: 1,
                          transition: 'width 0.2s ease',
                        },
                        '&:hover::after': {
                          width: '60%',
                        },
                      }}
                    >
                      {link.label}
                    </Button>
                  )
                )}

                <Box sx={{ width: 16 }} />

                <Button
                  component={Link}
                  to="/contact"
                  variant="contained"
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'white',
                    px: 2.5,
                    py: 1,
                    minHeight: 40,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: 4,
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      boxShadow: 6,
                    },
                  }}
                >
                  Contact Sales
                </Button>

                <Button
                  href="https://app.georoad.ai/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  sx={{
                    px: 2.5,
                    py: 1,
                    minHeight: 40,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    borderWidth: 1.5,
                    borderColor: (theme) => showTransparent
                      ? alpha('#fff', 0.5)
                      : theme.palette.primary.main,
                    color: (theme) => showTransparent ? 'white' : theme.palette.primary.main,
                    '&:hover': {
                      borderWidth: 1.5,
                      borderColor: (theme) => showTransparent
                        ? 'white'
                        : theme.palette.primary.dark,
                      bgcolor: (theme) => showTransparent
                        ? alpha('#fff', 0.1)
                        : alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  Log In
                </Button>
              </Box>
            )}

            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                edge="end"
                onClick={handleDrawerToggle}
                sx={{
                  color: showTransparent ? 'white' : 'text.primary',
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        PaperProps={{
          sx: { width: '100%', maxWidth: 320 },
        }}
      >
        {drawer}
      </Drawer>

      {/* Spacer for fixed navbar */}
      <Toolbar sx={{ minHeight: { xs: 64, md: 72 } }} />
    </>
  );
};

export default Navbar;
